import { useEffect, useState } from 'react';
import type { IconName } from '../components/Icon.tsx';
import { t, type MessageKey } from '../i18n/index.ts';
import { action } from './api.ts';

/** Approvals: documents a business wants a second person to say yes to before they go out. */

export type ApprovalDoc = 'invoicing.invoice' | 'purchase.order' | 'purchase.bill' | 'payroll.run';
export type ApprovalState = 'pending' | 'approved' | 'rejected' | 'withdrawn';

/** What the server sends when a step waits for approval (in an error's details). */
export interface ApprovalBlock {
  ok: false;
  document: ApprovalDoc;
  record: string;
  state: 'none' | 'pending' | 'rejected';
  message: string;
  approval?: string;
  approver?: string;
}

export interface Approval {
  id: string;
  document: ApprovalDoc;
  record: string;
  rule: string;
  amount: number;
  label: string;
  state: ApprovalState;
  requested_by: string | null;
  requested_by_name: string | null;
  requested_at: string;
  note: string | null;
  decided_by: string | null;
  decided_by_name: string | null;
  decided_at: string | null;
  reason: string | null;
  approver_role: string | null;
  approver_name: string | null;
  mine?: boolean;
}

export const DOC_LABEL: Record<ApprovalDoc, MessageKey> = {
  'invoicing.invoice': 'appr.d_invoice',
  'purchase.order': 'appr.d_order',
  'purchase.bill': 'appr.d_bill',
  'payroll.run': 'appr.d_payroll',
};
/** The step that waits, e.g. "Invoices, before they are issued". */
export const DOC_STEP: Record<ApprovalDoc, MessageKey> = {
  'invoicing.invoice': 'appr.s_invoice',
  'purchase.order': 'appr.s_order',
  'purchase.bill': 'appr.s_bill',
  'payroll.run': 'appr.s_payroll',
};
export const DOC_ICON: Record<ApprovalDoc, IconName> = {
  'invoicing.invoice': 'invoice',
  'purchase.order': 'bag',
  'purchase.bill': 'receipt',
  'payroll.run': 'cash',
};

/** Where the document opens. */
export function docLink(doc: ApprovalDoc, record: string): string {
  const id = encodeURIComponent(record);
  switch (doc) {
    case 'invoicing.invoice':
      return `/invoices?open=${id}`;
    case 'purchase.order':
      return `/manage/purchasing?open=${id}`;
    case 'purchase.bill':
      return `/manage/purchasing?tab=bills&open=${id}`;
    case 'payroll.run':
      return `/payroll?open=${id}`;
  }
}

const ROLE_LABEL: Record<string, MessageKey> = {
  owner: 'role.owner',
  manager: 'role.manager',
  accountant: 'role.accountant',
  sales: 'role.sales',
  cashier: 'role.cashier',
  waiter: 'role.waiter',
  kitchen: 'role.kitchen',
};
export const roleName = (r: string) => (ROLE_LABEL[r] ? t(ROLE_LABEL[r]) : r.charAt(0).toUpperCase() + r.slice(1).replace(/_/g, ' '));

/** "Teuta Hoxha" or "Any manager". */
export const approverText = (a: { approver_role: string | null; approver_name: string | null }) =>
  a.approver_name ?? (a.approver_role ? t('appr.any_role', { role: roleName(a.approver_role) }) : '—');

/** Tells the menu badge to count again after asking or answering. */
export const approvalsChanged = () => window.dispatchEvent(new Event('erp:approvals'));

/** How many requests wait for this person's answer, and how many they asked for lately. Checked every minute. */
export function useApprovalCount(enabled: boolean) {
  const [count, setCount] = useState({ waiting: 0, asked: 0 });
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const load = () =>
      void action<{ waiting: number; asked: number }>('studio.approval_count').then(
        (c) => alive && setCount(c),
        () => undefined,
      );
    load();
    const id = setInterval(load, 60_000);
    window.addEventListener('erp:approvals', load);
    return () => {
      alive = false;
      clearInterval(id);
      window.removeEventListener('erp:approvals', load);
    };
  }, [enabled]);
  return count;
}
